import { isAxiosError } from 'axios';
import axiosInstance from './axiosCreator';
import { IMPERSONATION_FORBIDDEN_CODE } from './accountSecurity';
import { store } from '../store/store';
import { normalizeFlashcard } from '../utils/flashcardVariants';
import type { Flashcard } from '../types/Flashcard';
import type { FlashcardsState, FlashcardScheduling } from '../types/Flashcard';
import {
  type FlashcardPendingResponse,
  type FlashcardRunCardType,
  type FlashcardRunOrder,
  type FlashcardRunScope,
  flashcardRunBootstrapped,
  flashcardRunHydrated,
  flashcardRunPendingAttempted,
  flashcardRunResponseDropped,
  flashcardRunResponseFailed,
  flashcardRunResponsePersisted,
} from '../slices/flashcardRun/flashcardRunSlice';

type FlashcardRunBootstrapResponse = {
  scopeKey: string;
  studySessionId: string | null;
  priorityDueIds: string[];
  newQueueIds: string[];
  deferredDueIds: string[];
  completedIds?: string[];
  currentCardId: string | null;
  currentCardType: 'due' | 'new' | 'none';
  order?: FlashcardRunOrder;
  completedCount: number;
  scheduledCount?: number;
  suspendedCount?: number;
  totalCount: number;
  linkedTodayCount?: number;
  linkedCount?: number;
  hydratedCards: Flashcard[];
};

export type SessionFlashcardRunCounts = {
  newCount: number;
  dueCount: number;
  completedCount: number;
  scheduledCount: number;
  suspendedCount: number;
  totalCount: number;
  // Redux-derived count snapshots (e.g. the today-tile cache) omit these.
  linkedTodayCount?: number;
  linkedCount?: number;
};

export type SessionFlashcardRunOptions = {
  amount?: 'all' | 'count';
  count?: number;
  type?: 'new' | 'due' | 'both' | 'linked';
  order?: FlashcardRunOrder;
};

const buildSessionFlashcardRunSearch = (
  options?: SessionFlashcardRunOptions
) => {
  const params = new URLSearchParams();
  if (options?.amount) {
    params.set('amount', options.amount);
  }
  if (options?.type) {
    params.set('type', options.type);
  }
  if (options?.order) {
    params.set('order', options.order);
  }
  if (options?.amount === 'count' && options.count) {
    params.set('count', String(options.count));
  }
  const search = params.toString();
  return search ? `?${search}` : '';
};

const mapBootstrapPayload = (payload: FlashcardRunBootstrapResponse) => ({
  ...payload,
  hydratedCards: (payload.hydratedCards ?? []).map(normalizeFlashcard),
});

export const fetchSessionFlashcardRunCounts = async (
  studySessionId: string,
  options?: SessionFlashcardRunOptions
): Promise<SessionFlashcardRunCounts> => {
  const response = await axiosInstance.get(
    `/api/v5/study/session/${studySessionId}/flashcard-run/counts${buildSessionFlashcardRunSearch(options)}`
  );

  return (response.data?.data ?? response.data) as SessionFlashcardRunCounts;
};

const bootstrapSessionFlashcardRunAttempt = async (
  studySessionId: string,
  options: SessionFlashcardRunOptions | undefined,
  retryAfterInvalidation: boolean
) => {
  const bootstrapGeneration =
    store.getState().flashcardRun.bootstrapGeneration ?? 0;

  const response = await axiosInstance.get(
    `/api/v5/study/session/${studySessionId}/flashcard-run${buildSessionFlashcardRunSearch(options)}`
  );

  const payload = mapBootstrapPayload(response.data?.data ?? response.data);

  if (
    (store.getState().flashcardRun.bootstrapGeneration ?? 0) !==
    bootstrapGeneration
  ) {
    if (retryAfterInvalidation) {
      return bootstrapSessionFlashcardRunAttempt(
        studySessionId,
        options,
        false
      );
    }
    return payload;
  }

  store.dispatch(
    flashcardRunBootstrapped({
      scope: {
        kind: 'session',
        studySessionId,
        amount: options?.amount,
        count: options?.count,
        type: options?.type,
        order: options?.order,
      },
      ...payload,
    })
  );

  return payload;
};

export const bootstrapSessionFlashcardRun = async (
  studySessionId: string,
  options?: SessionFlashcardRunOptions
) => bootstrapSessionFlashcardRunAttempt(studySessionId, options, true);

/**
 * Start a run that is not the Today session: a filter/lecture scope, or a study
 * set served in chat (ORA-681). The endpoint takes the scope as it is written
 * here, so a set needs nothing but its id.
 */
const bootstrapStandaloneFlashcardRunAttempt = async (
  scope: Exclude<FlashcardRunScope, { kind: 'session' }>,
  retryAfterInvalidation: boolean
) => {
  const bootstrapGeneration =
    store.getState().flashcardRun.bootstrapGeneration ?? 0;

  const response = await axiosInstance.post('/api/v5/flashcards/run', {
    scope,
  });

  const payload = mapBootstrapPayload(response.data?.data ?? response.data);

  if (
    (store.getState().flashcardRun.bootstrapGeneration ?? 0) !==
    bootstrapGeneration
  ) {
    if (retryAfterInvalidation) {
      return bootstrapStandaloneFlashcardRunAttempt(scope, false);
    }
    return payload;
  }

  store.dispatch(
    flashcardRunBootstrapped({
      scope,
      ...payload,
    })
  );

  return payload;
};

export const bootstrapStandaloneFlashcardRun = async (
  scope: Exclude<FlashcardRunScope, { kind: 'session' }>
) => bootstrapStandaloneFlashcardRunAttempt(scope, true);

export const bootstrapFlashcardRunFromLegacyCards = (params: {
  scope: FlashcardRunScope;
  flashcards: FlashcardsState;
  scopeKey: string;
}) => {
  const newQueueIds = (params.flashcards.new ?? []).map((card) => card.id);
  const priorityDueIds = (params.flashcards.due ?? []).map((card) => card.id);

  const completedIds = (params.flashcards.completed ?? []).map(
    (card) => card.id
  );

  const hydratedCards = [
    ...(params.flashcards.new ?? []),
    ...(params.flashcards.due ?? []),
    ...(params.flashcards.completed ?? []),
  ].map(normalizeFlashcard);

  const currentCardType: FlashcardRunCardType =
    priorityDueIds.length > 0 ? 'due' : newQueueIds.length > 0 ? 'new' : 'none';

  const payload = {
    scope: params.scope,
    scopeKey: params.scopeKey,
    studySessionId:
      params.scope.kind === 'session' ? params.scope.studySessionId : null,
    priorityDueIds,
    newQueueIds,
    deferredDueIds: [] as string[],
    completedIds,
    currentCardId: priorityDueIds[0] ?? newQueueIds[0] ?? null,
    currentCardType,
    completedCount: completedIds.length,
    totalCount:
      priorityDueIds.length + newQueueIds.length + completedIds.length,
    hydratedCards,
  };

  store.dispatch(flashcardRunBootstrapped(payload));
  return payload;
};

type HydrationContext = {
  userId: string | null;
  scopeKey: string | null;
  runId: string | null;
};

const inFlightHydrations = new Map<string, Map<string, Promise<Flashcard[]>>>();

const getHydrationContext = (): HydrationContext => {
  const state = store.getState();
  return {
    userId: state.auth.userId,
    scopeKey: state.flashcardRun.scopeKey,
    runId: state.flashcardRun.hydrationRunId,
  };
};

const hydrationContextKey = (context: HydrationContext) =>
  JSON.stringify([
    context.userId,
    // A run id is unique to one in-memory bootstrap and deliberately survives
    // cache invalidation. Fall back to the scope for persisted pre-run-id state.
    context.runId ? ['run', context.runId] : ['scope', context.scopeKey],
  ]);

const hydrationContextMatches = (context: HydrationContext) =>
  hydrationContextKey(context) === hydrationContextKey(getHydrationContext());

const clearCompletedHydration = (
  contextKey: string,
  flashcardIds: string[],
  request: Promise<Flashcard[]>
) => {
  const requestsById = inFlightHydrations.get(contextKey);
  if (!requestsById) return;

  flashcardIds.forEach((flashcardId) => {
    if (requestsById.get(flashcardId) === request) {
      requestsById.delete(flashcardId);
    }
  });

  if (requestsById.size === 0) {
    inFlightHydrations.delete(contextKey);
  }
};

export const hydrateFlashcardRunCards = async (flashcardIds: string[]) => {
  const normalizedIds = Array.from(new Set(flashcardIds.filter(Boolean)));
  if (normalizedIds.length === 0) {
    return [] as Flashcard[];
  }

  const state = store.getState();

  const missingIds = normalizedIds.filter(
    (flashcardId) => !state.flashcardRun.cardsById[flashcardId]
  );

  if (missingIds.length === 0) {
    return normalizedIds
      .map((flashcardId) => state.flashcardRun.cardsById[flashcardId])
      .filter(Boolean);
  }

  const context = getHydrationContext();
  const contextKey = hydrationContextKey(context);

  const requestsById =
    inFlightHydrations.get(contextKey) ??
    new Map<string, Promise<Flashcard[]>>();

  inFlightHydrations.set(contextKey, requestsById);

  const requests = new Set<Promise<Flashcard[]>>();
  const idsToFetch: string[] = [];

  missingIds.forEach((flashcardId) => {
    const inFlight = requestsById.get(flashcardId);
    if (inFlight) {
      requests.add(inFlight);
    } else {
      idsToFetch.push(flashcardId);
    }
  });

  if (idsToFetch.length > 0) {
    const request = axiosInstance
      .post('/api/v5/flashcards/hydrate', { flashcardIds: idsToFetch })
      .then((response) => {
        const hydratedCards = (
          (response.data?.data ?? response.data) as Flashcard[]
        ).map(normalizeFlashcard);

        if (hydrationContextMatches(context)) {
          store.dispatch(flashcardRunHydrated(hydratedCards));
        }

        return hydratedCards;
      });

    idsToFetch.forEach((flashcardId) => {
      requestsById.set(flashcardId, request);
    });

    void request.then(
      () => clearCompletedHydration(contextKey, idsToFetch, request),
      () => clearCompletedHydration(contextKey, idsToFetch, request)
    );

    requests.add(request);
  }

  return (await Promise.all(requests)).flat();
};

const RESPOND_REQUEST_TIMEOUT_MS = 20_000;

/** One rating, as the respond endpoint takes it. */
export interface FlashcardRespondPayload {
  flashcardId: string;
  response: number;
  scheduling: FlashcardScheduling;
  duration?: number | null;
  /** Omitted outside a study session — an in-chat rating has none. */
  studySessionId?: string;
  flashcardVariantId?: string;
  /** The study set the card was rated in (ORA-677), when it came from one. */
  studySetId?: string;
}

/**
 * Write one flashcard rating. The run's queue flushes through here, and so does
 * the in-chat study-set deck, which has no session to queue against — one place
 * that knows the endpoint's body.
 */
export const postFlashcardResponse = async (
  payload: FlashcardRespondPayload
) => {
  const {
    flashcardId,
    response,
    scheduling,
    duration,
    studySessionId,
    flashcardVariantId,
    studySetId,
  } = payload;

  return axiosInstance.post(
    `/api/v5/flashcards/${encodeURIComponent(flashcardId)}/respond`,
    {
      response,
      studySessionId,
      duration,
      scheduling,
      flashcardVariantId,
      ...(studySetId ? { studySetId } : {}),
    },
    // The flush writes one rating at a time, so a request that never answers
    // (a dead socket on mobile) would otherwise hold every later rating until
    // the browser gives up on it. A timed-out rating stays queued and retries.
    { timeout: RESPOND_REQUEST_TIMEOUT_MS }
  );
};

const persistFlashcardResponse = async (pending: FlashcardPendingResponse) =>
  postFlashcardResponse({
    flashcardId: pending.flashcardId,
    response: pending.responseNum,
    studySessionId: pending.studySessionId,
    duration: pending.duration,
    scheduling: pending.scheduling,
    flashcardVariantId: pending.flashcardVariantId,
    studySetId: pending.studySetId,
  });

/**
 * One flush at a time. Every rating, the 30 s timer and `online` all call the
 * flush, and overlapping loops posted the same pending entry twice — duplicate
 * response rows in production, a fraction of a second apart. A call that lands
 * mid-flush asks for one more pass instead, so its rating still goes out
 * promptly.
 */
let inFlightFlush: Promise<void> | null = null;
let shouldFlushAgain = false;
// Statuses the respond endpoint answers for a rating it will never accept: a
// malformed or session-less request (400), a deleted card (404), and the
// impersonation refusal (403 with its own code). Retrying those forever would
// also hold back every later rating of the same card, so they are dropped
// instead. Anything else — network, 5xx, 401 while the session
// re-establishes, 429, and the other 403s a route can answer with (untrusted
// origin, two-factor setup required), which clear once the deploy or the
// account is fixed — stays queued and retries.
const TERMINAL_RESPOND_STATUSES = new Set([400, 404]);

const isTerminalRespondError = (error: unknown) => {
  if (!isAxiosError(error)) return false;
  const status = error.response?.status;
  if (status === 403) {
    return (
      (error.response?.data as { code?: unknown } | undefined)?.code ===
      IMPERSONATION_FORBIDDEN_CODE
    );
  }
  return TERMINAL_RESPOND_STATUSES.has(status ?? 0);
};

// The one terminal rejection that still carries a rating worth keeping: the
// session the rating was tagged with no longer resolves for this user. The
// scheduling and the response row matter more than the session attribution,
// so the rating is written once more without it before anything is dropped.
const isInvalidSessionError = (error: unknown) =>
  isAxiosError(error) &&
  error.response?.status === 400 &&
  (error.response.data as { error?: unknown } | undefined)?.error ===
    'Invalid studySessionId';

const persistPendingResponse = async (pending: FlashcardPendingResponse) => {
  try {
    await persistFlashcardResponse(pending);
  } catch (error) {
    if (!pending.studySessionId || !isInvalidSessionError(error)) {
      throw error;
    }

    console.warn(
      'Flashcard response session no longer valid; saving without it:',
      pending.flashcardId
    );

    await persistFlashcardResponse({ ...pending, studySessionId: undefined });
  }
};

const flushPendingFlashcardResponsesPass = async () => {
  // The queue is newest-first (the slice unshifts), but the respond endpoint is
  // last-write-wins: posting newest-first let a retried older rating overwrite
  // the card's newer scheduling. Post oldest-first, and once a card's entry
  // fails, hold its later entries until the next pass so they never jump it.
  const startState = store.getState();

  const pendingResponses = [
    ...startState.flashcardRun.pendingResponses,
  ].reverse();

  const userIdAtStart = startState.auth.userId;
  const failedFlashcardIds = new Set<string>();

  for (const pending of pendingResponses) {
    if (failedFlashcardIds.has(pending.flashcardId)) continue;

    // The pass works from a snapshot. Stop if the account changed under it —
    // a rating must never be written under another user's cookie — and skip
    // an entry the store has since let go of.
    const currentState = store.getState();
    if (currentState.auth.userId !== userIdAtStart) return;
    if (
      !currentState.flashcardRun.pendingResponses.some(
        (entry) => entry.requestId === pending.requestId
      )
    ) {
      continue;
    }

    try {
      store.dispatch(
        flashcardRunPendingAttempted({ requestId: pending.requestId })
      );

      await persistPendingResponse(pending);

      store.dispatch(
        flashcardRunResponsePersisted({ requestId: pending.requestId })
      );
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : 'Failed to persist flashcard response';

      if (isTerminalRespondError(error)) {
        console.error(
          'Dropping a flashcard response the server rejected:',
          pending.flashcardId,
          message
        );

        store.dispatch(
          flashcardRunResponseDropped({ requestId: pending.requestId })
        );

        continue;
      }

      failedFlashcardIds.add(pending.flashcardId);

      store.dispatch(
        flashcardRunResponseFailed({
          requestId: pending.requestId,
          error: message,
        })
      );
    }
  }
};

export const flushPendingFlashcardResponses = async (): Promise<void> => {
  if (inFlightFlush) {
    shouldFlushAgain = true;
    return inFlightFlush;
  }

  inFlightFlush = (async () => {
    try {
      do {
        shouldFlushAgain = false;
        await flushPendingFlashcardResponsesPass();
      } while (shouldFlushAgain);
    } finally {
      inFlightFlush = null;
    }
  })();

  return inFlightFlush;
};
